<script setup lang="ts">
import { computed, reactive, ref, watch, h, onMounted } from 'vue';
import { getCurrentLocale } from '@/i18n';
import { useI18n } from 'vue-i18n';
import { helpers, required } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { useRoute, useRouter } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import routerKey from '@/router/routerKey';
import { AIR_ONLY, HOTEL_ONLY, LANG_ENGLISH, CRUISE, PACKAGE, MULTI_TOURS, CUSTOMER_FORM, TOUR, LANG_FRENCH } from '@/constants';
import { useAdminSetupStore } from '@/store/AdminSetupStore';
import { useComponentComposable } from '@/components/useComponentComposable';
import { useGatewayStore } from '@/store/GatewayStore';
import { useGroupTypeStore } from '@/store/GroupTypeStore';
import { useHotelManagementStore } from '@/store/HotelManagementStore';
import { usePromosStore } from '@/store/PromosStore';
import { useTourManagementStore } from '@/store/TourManagementStore';
import { useTermsAndConditionsStore } from '@/store/TermsAndConditionsStore';
import { useUserAuthorizationsStore } from '@/store/UserAuthorizationsStore';
import { useViewQuoteStore } from '@/store/ViewQuoteStore';
import { publishMpvAnalytic } from '@/modules/analytics/analyticsService';
import Select from 'primevue/select';
import Divider from 'primevue/divider';
import InputText from 'primevue/inputtext';
import RadioButton from 'primevue/radiobutton';
import Tab from 'primevue/tab';
import TabList from 'primevue/tablist';
import TabPanel from 'primevue/tabpanel';
import TabPanels from 'primevue/tabpanels';
import Tabs from 'primevue/tabs';
import AccordionPromo from '@/components/promos/AccordionPromo.vue';
import AdvancedPackageTab from '@/components/tabs/AdvancedPackageTab.vue';
import AirTab from '@/components/tabs/AirTab.vue';
import CruiseTab from '@/components/tabs/CruiseTab.vue';
import FormPromo from '@/components/promos/FormPromo.vue';
import HotelTab from '@/components/tabs/HotelTab.vue';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import MultiDayTourTab from '@/components/tabs/MultiDayToursTab.vue';
import PromoBoxes from '@/components/promos/PromoBoxes.vue';
import SurfacePromo from '@/components/promos/SurfacePromo.vue';
import TopBanner from '@/components/promos/TopBanner.vue';

const toast = useToast();
const adminSetupStore = useAdminSetupStore();
const { renderHTMLContent, renderLanguages } = useComponentComposable();
const gatewayStore = useGatewayStore();
const groupTypeManagement = useGroupTypeStore();
const viewQuoteStore = useViewQuoteStore();
const quote = computed(() => viewQuoteStore.selectQuote);
const promosStore = usePromosStore();
const currentLanguage = getCurrentLocale();
const hotelManagementStore = useHotelManagementStore();
const termsAndConditionsStore = useTermsAndConditionsStore();
const lang = currentLanguage.replace(/^\w/, (c) => c.toUpperCase());
const tourManagementStore = useTourManagementStore();
const isRequote = computed(() => viewQuoteStore.selectIsRequote);
const userAuthorizationsStore = useUserAuthorizationsStore();
const authorizations: any = userAuthorizationsStore.permissionByRole;

const advancedForm = reactive([
  {
    groupName: '',
    groupContact: '',
    groupType: '',
    groupTypeId: '',
    groupTypeFr: '',
    instaQuote: ''
  }
]);

const { t } = useI18n({ useScope: 'global' });

const selectedLanguage = ref(currentLanguage);
const isValidation = ref(false);
const isShowError = ref(false);
const isPackageTab = ref(true);
const isLoading = ref(true);

const router = useRouter();
const route = useRoute();

const rules = {
  $each: helpers.forEach({
    groupName: {
      required: helpers.withMessage(t('validate.required'), required)
    },
    groupContact: {
      required: helpers.withMessage(t('validate.required'), required)
    },
    groupTypeId: {
      required: helpers.withMessage(t('validate.required'), required)
    }
  })
};
const sectionTitle = reactive({
  promoBoxTitleEn: '',
  promoBoxTitleFr: ''
});
const v$ = useVuelidate(rules, advancedForm);
const groupTypeList = ref([]);
const calendarConfigData = ref({});
const avgPaxRoomData = ref(2.2);
const getAvgPaxRoom = () => {
  hotelManagementStore.getAvgPaxRoom().then((response) => {
    avgPaxRoomData.value = response.data?.avgPaxRoom;
  });
};
getAvgPaxRoom();
const handleSubmit = () => {
  isShowError.value = true;
  if (advancedForm[0].groupTypeId && advancedForm[0].groupName && advancedForm[0].groupContact) {
    isValidation.value = true;
  } else {
    isValidation.value = false;
  }
};

const handleUpdateFormPromo = (tab: number) => {
  let data;
  if (listFormPromos.value.length > 0) {
    switch (tab) {
      case 0:
        data = listFormPromos.value.filter((item) => item.qType === PACKAGE);
        formPromoInfo.value = data;
        break;
      case 1:
        data = listFormPromos.value.filter((item) => item.qType === HOTEL_ONLY);
        formPromoInfo.value = data;
        break;
      case 2:
        data = listFormPromos.value.filter((item) => item.qType === AIR_ONLY);
        formPromoInfo.value = data;
        break;
      case 3:
        data = listFormPromos.value.filter((item) => item.qType === CRUISE);
        formPromoInfo.value = data;
        break;
      case 4:
        data = listFormPromos.value.filter((item) => item.qType === MULTI_TOURS);
        formPromoInfo.value = data;
        break;
      default:
        break;
    }
  }
};

const clearDataAdvancedForm = () => {
  advancedForm[0].groupName = '';
  advancedForm[0].groupContact = '';
  advancedForm[0].groupType = '';
  advancedForm[0].groupTypeId = '';
  advancedForm[0].groupTypeFr = '';
  advancedForm[0].instaQuote = '';
  isShowError.value = false;
  isValidation.value = false;
};

const tabChange = (index: number) => {
  isPackageTab.value = index === 0;
  getDepartureData();
  handleUpdateFormPromo(index);
  if (index == 1) {
    getQuoteFormIntro(HOTEL_ONLY);
    router.push({ name: routerKey.HOTEL });
  } else if (index == 2) {
    getQuoteFormIntro(AIR_ONLY);
    router.push({ name: routerKey.AIR });
  } else if (index == 3) {
    getQuoteFormIntro(CRUISE);
    router.push({ name: routerKey.CRUISE });
  } else if (index == 4) {
    getQuoteFormIntro(TOUR);
    router.push({ name: routerKey.MULTI_TOURS });
  } else {
    getQuoteFormIntro(PACKAGE);
    router.push({ name: routerKey.HOME });
  }
};

const handleActive = (): number => {
  switch (route.name) {
    case routerKey.HOTEL:
      return 1;
    case routerKey.AIR:
      return 2;
    case routerKey.CRUISE:
      return 3;
    case routerKey.MULTI_TOURS:
      return 4;
    default:
      return 0;
  }
};

const topBannerInfo = ref([]);
const surfacePromoInfo = ref([]);
const formPromoInfo = ref([]);
const listFormPromos = ref([]);
const accordionPromo = ref([]);
const bindingPromoBox = ref([]);
const quoteIntro = ref({});
const tabsHeader = ref([
  { title: t('advance_quote.form.package'), value: 0, routeKey: routerKey.HOME, isShowTab: authorizations?.advance_quote?.package_insta_create },
  { title: t('advance_quote.form.hotel'), value: 1, routeKey: routerKey.HOTEL, isShowTab: authorizations?.advance_quote?.hotel_manual_create },
  { title: t('advance_quote.form.air'), value: 2, routeKey: routerKey.AIR, isShowTab: authorizations?.advance_quote?.air_manual_create },
  { title: t('advance_quote.form.cruise'), value: 3, routeKey: routerKey.CRUISE, isShowTab: authorizations?.advance_quote?.cruise_manual_create },
  { title: t('advance_quote.form.multi_day_tour'), value: 4, routeKey: routerKey.MULTI_TOURS, isShowTab: authorizations?.advance_quote?.tour_create }
]);

const visibleTabs = computed(() => tabsHeader.value.filter((tab: any) => tab.isShowTab));

const bindingTopBanner = computed(() => ({
  name: topBannerInfo.value[0]['title' + lang],
  subTitle: topBannerInfo.value[0]['subTitle' + lang],
  description: topBannerInfo.value[0]['desc' + lang],
  image: topBannerInfo.value[0]['imgUrl' + lang],
  imageTitle: topBannerInfo.value[0]['imgTitle' + lang],
  renderImage: topBannerInfo.value[0]['image' + lang]
}));
const bindingSurfacePromo = computed(() => ({
  name: surfacePromoInfo.value[0]['title' + lang],
  subTitle: surfacePromoInfo.value[0]['subTitle' + lang],
  description: surfacePromoInfo.value[0]['desc' + lang],
  image: surfacePromoInfo.value[0]['imgUrl' + lang],
  term: surfacePromoInfo.value[0]['terms' + lang],
  imageTitle: surfacePromoInfo.value[0]['imgTitle' + lang],
  nameFirstButton: surfacePromoInfo.value[0]['nameFirstButton' + lang],
  urlFirstButton: surfacePromoInfo.value[0]['urlFirstButton' + lang],
  nameSecondButton: surfacePromoInfo.value[0]['nameSecondButton' + lang],
  urlSecondButton: surfacePromoInfo.value[0]['urlSecondButton' + lang],
  renderImage: surfacePromoInfo.value[0]['image' + lang]
}));

const bindingFormPromo = computed(() => {
  return [
    {
      title: formPromoInfo.value[0]['topTitle' + lang],
      description: formPromoInfo.value[0]['topDesc' + lang],
      TC: formPromoInfo.value[0]['topTC' + lang],
      renderImage: formPromoInfo.value[0]['topImage' + lang]
    },
    {
      title: formPromoInfo.value[0]['bottomTitle' + lang],
      description: formPromoInfo.value[0]['bottomDesc' + lang],
      TC: formPromoInfo.value[0]['bottomTC' + lang],
      renderImage: formPromoInfo.value[0]['bottomImage' + lang]
    }
  ];
});

const getSectionTitlePromo = () => {
  promosStore.getSectionTitlePromo().then((res) => {
    sectionTitle.promoBoxTitleEn = res.promoBoxTitleEn;
    sectionTitle.promoBoxTitleFr = res.promoBoxTitleFr;
    sectionTitle.accordionPromoTitleFr = res.accordionPromoTitleFr;
    sectionTitle.accordionPromoTitleEn = res.accordionPromoTitleEn;
    sectionTitle.surfacePromoTitleFr = res.surfacePromoTitleFr;
    sectionTitle.surfacePromoTitleEn = res.surfacePromoTitleEn;
  });
};

const getgroupTypeData = async () => {
  const response = await groupTypeManagement.getGroupTypeList();
  groupTypeList.value = response.data;
};

const getDepartureData = () => {
  isLoading.value = true;
  adminSetupStore
    .getDepartureConfig()
    .then((res: any) => {
      calendarConfigData.value = res;
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.unexpected_error'), life: 5000 });
    })
    .finally(() => (isLoading.value = false));
};

getDepartureData();
getSectionTitlePromo();
gatewayStore.getGateways();
tourManagementStore.getGateways();
adminSetupStore.getConfigOption();
adminSetupStore.getMinimumConfig();

const getQuoteFormIntro = (qtype: string) => {
  isLoading.value = true;
  termsAndConditionsStore
    .getData(CUSTOMER_FORM, qtype)
    .then((res) => {
      quoteIntro.value = res;
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.unexpected_error'), life: 5000 });
    })
    .finally(() => {
      isLoading.value = false;
    });
};
getQuoteFormIntro(quote.value?.qType ? quote.value.qType : PACKAGE);

const renderIntroHtml = () => {
  if (currentLanguage === LANG_FRENCH) {
    return h('div', { class: 'french-term', innerHTML: quoteIntro.value.introFr });
  } else {
    return h('div', { class: 'english-term', innerHTML: quoteIntro.value.intro });
  }
};

const handleRequote = async () => {
  await getgroupTypeData();
  if (quote.value && isRequote.value) {
    const groupType = groupTypeList.value.find((g) => g.sk === quote.value?.gTypeId);
    advancedForm[0].groupContact = quote.value.gContact;
    advancedForm[0].groupName = quote.value.gName;
    advancedForm[0].groupTypeId = quote.value.gTypeId;
    advancedForm[0].groupType = quote.value.gType;
    advancedForm[0].groupTypeFr = quote.value.gTypeFr;
    advancedForm[0].instaQuote = groupType?.instaQuote;
  }
};
const changeGroupType = (e) => {
  const groupType = groupTypeList.value.find((g) => g.sk === e.value);
  advancedForm[0].groupType = groupType?.name;
  advancedForm[0].groupTypeFr = groupType?.nameFr;
  advancedForm[0].instaQuote = groupType?.instaQuote;
};
const showQuoteForm =
  authorizations?.advance_quote?.package_insta_create ||
  authorizations?.advance_quote?.hotel_manual_create ||
  authorizations?.advance_quote?.air_manual_create ||
  authorizations?.advance_quote?.cruise_manual_create ||
  authorizations?.advance_quote?.tour_create;

const getTopbarInfo = () => {
  promosStore.getTopbarInfo().then((res) => {
    topBannerInfo.value = res;
  });
};

const getFormPromo = () => {
  promosStore.getFormPromo().then((res) => {
    switch (route.name) {
      case routerKey.HOME:
        formPromoInfo.value = res.filter((item: any) => item.qType === PACKAGE);
        break;
      case routerKey.HOTEL:
        formPromoInfo.value = res.filter((item: any) => item.qType === HOTEL_ONLY);
        break;
      case routerKey.AIR:
        formPromoInfo.value = res.filter((item: any) => item.qType === AIR_ONLY);
        break;
      case routerKey.CRUISE:
        formPromoInfo.value = res.filter((item: any) => item.qType === CRUISE);
        break;
      case routerKey.MULTI_TOURS:
        formPromoInfo.value = res.filter((item: any) => item.qType === MULTI_TOURS);
        break;
      default:
        break;
    }
    listFormPromos.value = res;
  });
};

const getSurfacePromos = () => {
  promosStore.getSurfacePromos().then((res) => {
    surfacePromoInfo.value = res;
  });
};

const getPromoBox = () => {
  promosStore.getPromoBox().then((res) => {
    res.sort((a, b) => a.order - b.order);
    const newList = res.map((item) => ({
      title: item['title' + lang],
      description: item['desc' + lang],
      image: item['imgUrl' + lang],
      terms: item['terms' + lang],
      isShow: item['showInB2b' + lang],
      renderImage: item['image' + lang],
      color: currentLanguage === LANG_ENGLISH ? item.colorEn : item.colorFr
    }));
    bindingPromoBox.value = newList;
  });
};

const getAccordionPromos = () => {
  promosStore.getAccordionPromos().then((res) => {
    accordionPromo.value = res
      .filter((item) => (lang.toLowerCase() === LANG_ENGLISH ? item.showInB2bEn : item.showInB2bFr))
      .sort((a, b) => a.order - b.order)
      .map((accordion) => ({
        title: accordion['title' + lang],
        subTitle: accordion['subTitle' + lang],
        description: accordion['desc' + lang],
        terms: accordion['terms' + lang],
        renderImage: accordion['image' + lang]
      }));
  });
};

onMounted(() => {
  publishMpvAnalytic('Group Quote Form');
  getTopbarInfo();
  getFormPromo();
  getPromoBox();
  getSurfacePromos();
  getAccordionPromos();
  handleRequote();
  getgroupTypeData();
});

watch(
  isRequote,
  () => {
    if (isRequote.value) {
      selectedLanguage.value = quote.value?.lang;
    }
  },
  { immediate: true }
);

watch(
  () => visibleTabs.value,
  (tabs) => {
    const currentRouteMatched = tabs.some((tab: any) => tab.routeKey === route.name);
    if (!currentRouteMatched && tabs.length > 0) {
      tabChange(tabs[0].value);
    }
  },
  { immediate: true }
);
</script>

<template>
  <div v-if="topBannerInfo?.length">
    <TopBanner :value="bindingTopBanner" />
  </div>
  <div class="grid advanced-search">
    <div class="col-12">
      <div v-if="showQuoteForm" class="card">
        <LoadingSpinner v-if="isLoading" />
        <div class="mb-5 title quote-form-intro">
          <renderIntroHtml v-if="quoteIntro"></renderIntroHtml>
        </div>

        <hr />
        <div class="flex">
          <div class="flex flex-col w-full gap-2 form">
            <div class="field grid gap-3">
              <div class="col">
                <div class="mb-1">
                  <label for="gName">{{ $t('advance_quote.form.group_name') }}</label>
                </div>
                <div>
                  <InputText class="w-full" id="gName" type="text" v-model="advancedForm[0].groupName" :class="isShowError && v$.$each.$response.$errors[0].groupName[0]?.$message ? 'p-invalid' : ''" />
                  <span class="text-red-500" v-if="isShowError">
                    {{ v$.$each.$response.$errors[0].groupName[0]?.$message }}
                  </span>
                </div>
              </div>
              <div class="col">
                <div class="mb-1">
                  <label for="gType">{{ $t('advance_quote.form.group_type') }}</label>
                </div>
                <div class="w-full">
                  <Select
                    v-model="advancedForm[0].groupTypeId"
                    :options="groupTypeList"
                    :optionLabel="currentLanguage === LANG_ENGLISH ? 'name' : 'nameFr'"
                    option-value="sk"
                    id="gType"
                    :placeholder="$t('advance_quote.form.select_group_type')"
                    @change="changeGroupType"
                    :class="isShowError && v$.$each.$response.$errors[0].groupTypeId[0]?.$message ? 'p-invalid w-full' : 'w-full'"
                  >
                    <template #option="slotProps">
                      <div class="max-w-20rem white-space-normal">
                        {{ currentLanguage === LANG_ENGLISH ? slotProps.option['name'] : slotProps.option['nameFr'] }}
                      </div>
                    </template>
                  </Select>
                  <span class="text-red-500" v-if="isShowError">
                    {{ v$.$each.$response.$errors[0].groupTypeId[0]?.$message }}
                  </span>
                </div>
              </div>
            </div>
            <div class="field grid gap-3">
              <div class="col">
                <div class="mb-1">
                  <label for="gcName">{{ $t('advance_quote.form.group_contact') }}</label>
                </div>
                <div>
                  <InputText class="w-full" id="gcName" type="text" v-model="advancedForm[0].groupContact" :class="isShowError && v$.$each.$response.$errors[0].groupContact[0]?.$message ? 'p-invalid' : ''" />
                  <span class="text-red-500" v-if="isShowError">
                    {{ v$.$each.$response.$errors[0].groupContact[0]?.$message }}
                  </span>
                </div>
              </div>
              <div class="col">
                <div class="mb-1">
                  <div>{{ $t('voucher.language') }}</div>
                </div>
                <div class="languages flex gap-4 mt-3 ml-2">
                  <div v-for="(language, index) in renderLanguages()" :key="index" class="flex align-items-center">
                    <label :for="language.label" class="mr-1">{{ language.label }}</label>
                    <RadioButton v-model="selectedLanguage" :inputId="language.label" name="language" :value="language.value" />
                  </div>
                </div>
              </div>
            </div>

            <Tabs :value="handleActive()" @update:value="tabChange">
              <TabList>
                <Tab v-for="tab in visibleTabs" :key="tab.title" :value="tab.value">{{ tab.title }}</Tab>
              </TabList>
              <TabPanels>
                <TabPanel :value="0" v-if="authorizations?.advance_quote?.package_insta_create">
                  <AdvancedPackageTab
                    @clear="clearDataAdvancedForm()"
                    @submit="handleSubmit()"
                    :advancedForm="advancedForm"
                    :isValidation="isValidation"
                    :avgPaxRoomData="avgPaxRoomData"
                    :selectedLanguage="selectedLanguage"
                    :quoteIntro="quoteIntro"
                    :calendarData="calendarConfigData.packageDeparture"
                  />
                </TabPanel>
                <TabPanel :value="1" v-if="authorizations?.advance_quote?.hotel_manual_create">
                  <HotelTab
                    @submit="handleSubmit()"
                    @clear="clearDataAdvancedForm()"
                    :advancedForm="advancedForm"
                    :isValidation="isValidation"
                    :avgPaxRoomData="avgPaxRoomData"
                    :selectedLanguage="selectedLanguage"
                    :quoteIntro="quoteIntro"
                    :calendarData="calendarConfigData.hotelDeparture"
                  />
                </TabPanel>
                <TabPanel :value="2" v-if="authorizations?.advance_quote?.air_manual_create">
                  <AirTab
                    @submit="handleSubmit()"
                    @clear="clearDataAdvancedForm()"
                    :advancedForm="advancedForm"
                    :isValidation="isValidation"
                    :avgPaxRoomData="avgPaxRoomData"
                    :selectedLanguage="selectedLanguage"
                    :quoteIntro="quoteIntro"
                    :calendarData="calendarConfigData.airDeparture"
                  />
                </TabPanel>
                <TabPanel :value="3" v-if="authorizations?.advance_quote?.cruise_manual_create">
                  <CruiseTab
                    @submit="handleSubmit()"
                    @clear="clearDataAdvancedForm()"
                    :advancedForm="advancedForm"
                    :isValidation="isValidation"
                    :avgPaxRoomData="avgPaxRoomData"
                    :selectedLanguage="selectedLanguage"
                    :quoteIntro="quoteIntro"
                    :calendarData="calendarConfigData.cruiseDeparture"
                  />
                </TabPanel>
                <TabPanel :value="4" v-if="authorizations?.advance_quote?.tour_create">
                  <MultiDayTourTab
                    @submit="handleSubmit()"
                    @clear="clearDataAdvancedForm()"
                    :advancedForm="advancedForm"
                    :isValidation="isValidation"
                    :avgPaxRoomData="avgPaxRoomData"
                    :selectedLanguage="selectedLanguage"
                    :quoteIntro="quoteIntro"
                    :calendarData="calendarConfigData.tourDeparture"
                  />
                </TabPanel>
              </TabPanels>
            </Tabs>
          </div>

          <div v-if="formPromoInfo?.length" class="m-3 desktop-show">
            <Divider layout="vertical"></Divider>
          </div>

          <div v-if="formPromoInfo?.length" class="align-items-center justify-content-center w-6 desktop-show">
            <FormPromo :value="bindingFormPromo" />
          </div>
        </div>
      </div>
    </div>
    <div class="col-12 mt-5" v-if="bindingPromoBox?.length">
      <PromoBoxes :value="bindingPromoBox" :section="sectionTitle" :horizon="true" />
    </div>
  </div>
  <div v-if="surfacePromoInfo?.length">
    <renderHTMLContent :data="currentLanguage === 'fr' ? sectionTitle.surfacePromoTitleFr : sectionTitle.surfacePromoTitleEn" htmlElement="h2"></renderHTMLContent>
    <SurfacePromo :value="bindingSurfacePromo" />
  </div>

  <div class="mt-6 mb-4" v-if="accordionPromo?.length">
    <renderHTMLContent :data="currentLanguage === 'fr' ? sectionTitle.accordionPromoTitleFr : sectionTitle.accordionPromoTitleEn" htmlElement="h2"></renderHTMLContent>
    <div class="mb-3" v-for="(accordion, index) in accordionPromo" :key="index">
      <AccordionPromo :value="accordion" />
    </div>
  </div>
</template>
